/* eslint-disable @typescript-eslint/no-empty-function */
import { FC, useEffect, useState, useMemo, useCallback } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X, Loader } from 'react-feather';
import styles from './DeactivateProductsDrawer.module.css';
import { DeactivateProductsDrawerProps } from './DeactivateProductsDrawer.types';
import { t } from 'i18next';
import { useRemoveProduct, useActiveBundleStoreGroups } from '@/services/menu/menus.service';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import { EditDrawerProcessing } from '../../SharedComponents/EditDrawerProcessing';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { PriceUpdateAPIResponse } from '@/types/stores.types';
import { useSTKContext } from '@/context/context';
import { getStores } from '@/services/menu/menus.helper.service';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';
import ProductsTable from './DeactivateProductsDrawerTabItems/ProductsTable';
import Icon from '@/components/Icons/Icon';
import {
  buildBlockedCellKeys,
  buildRemoveRequestPayload,
  collectStoreGroupCells,
  partitionStoreGroupCellsByActiveBundles,
} from './deactivateProducts.helper';
import { getSelectedStoreGroupNames } from '../StoreMenuModifiersDetails/modifierSelection.helpers';

export const DeactivateProductsDrawer: FC<DeactivateProductsDrawerProps> = ({
  isOpen,
  onClose,
  storeProducts,
  afterSave,
  setPriceDeviationsData,
}) => {
  const [state, setState] = useState(isOpen);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivationComplete, setDeactivationComplete] = useState(false);
  const [deactivationResponse, setDeactivationResponse] = useState<PriceUpdateAPIResponse | undefined>();
  const { storesAndGroups } = useSTKContext();

  const { mutate: removeProduct } = useRemoveProduct();

  const selectedStores = useMemo(() => getStores(storesAndGroups), [storesAndGroups]);

  const selectedVariantIds = useMemo(
    () => storeProducts.map((product) => product.variantId).filter(Boolean) as string[],
    [storeProducts],
  );

  const {
    data: activeBundleStoreGroups = [],
    isFetching: isFetchingBlockedState,
    isError: isBlockedStateError,
  } = useActiveBundleStoreGroups(isOpen && state, selectedVariantIds, selectedStores);

  const isBlockedStateReady = !isFetchingBlockedState && !isBlockedStateError;

  const resolvedActiveBundleStoreGroups = isBlockedStateReady ? activeBundleStoreGroups : [];

  const blockedCellKeys = useMemo(
    () => buildBlockedCellKeys(resolvedActiveBundleStoreGroups),
    [resolvedActiveBundleStoreGroups],
  );

  const storeGroupNames = useMemo(() => {
    const fromContext = getSelectedStoreGroupNames(storesAndGroups);

    if (fromContext.length > 0) {
      return fromContext;
    }

    const fromProducts = new Set<string>();
    storeProducts.forEach((product) => {
      product.storeGroups?.forEach((storeGroup) => {
        if (storeGroup.storeGroupName) {
          fromProducts.add(storeGroup.storeGroupName);
        }
      });
    });

    return Array.from(fromProducts);
  }, [storesAndGroups, storeProducts]);

  const resolveStoreGroupId = useCallback(
    (storeGroupName: string, product: (typeof storeProducts)[number]) => {
      const fromProduct = product.storeGroups?.find((sg) => sg.storeGroupName === storeGroupName)?.storeGroupId;
      if (fromProduct) {
        return fromProduct;
      }

      if (storesAndGroups?.origin === 'tab-stores-groups') {
        return storesAndGroups.storeGroups?.find((sg) => sg.storeGroup === storeGroupName)?.storeGroupId;
      }

      return undefined;
    },
    [storesAndGroups],
  );

  const storeGroupCells = useMemo(
    () => collectStoreGroupCells(storeProducts, storeGroupNames, resolveStoreGroupId),
    [storeProducts, storeGroupNames, resolveStoreGroupId],
  );

  const { blockedCells, eligibleCells } = useMemo(
    () => partitionStoreGroupCellsByActiveBundles(storeGroupCells, blockedCellKeys),
    [storeGroupCells, blockedCellKeys],
  );

  const hasBlocked = isBlockedStateReady && blockedCells.length > 0;
  const hasEligible =
    isBlockedStateReady &&
    (storeGroupCells.length === 0 ? storeProducts.some((product) => product.variantId) : eligibleCells.length > 0);

  const confirmationTitle = useMemo(() => {
    if (hasEligible && hasBlocked) {
      return t('deactivateProducts.confirmDeactivationEligible');
    }
    if (!hasEligible && hasBlocked) {
      return t('deactivateProducts.cannotDeactivateYetTitle');
    }
    return t('deactivateProducts.confirmDeactivation');
  }, [hasBlocked, hasEligible]);

  const infoText = useMemo(() => {
    if (!hasEligible && hasBlocked) {
      return t('deactivateProducts.cannotDeactivateYetInfo');
    }
    return t('deactivateProducts.deactivationInfoText');
  }, [hasBlocked, hasEligible]);

  useEffect(() => {
    setState(isOpen);
    if (!isOpen) {
      setIsDeactivating(false);
      setDeactivationComplete(false);
      setDeactivationResponse(undefined);
    }
  }, [isOpen]);

  // Build request payload from selected products
  // Only use stores that are selected in the context, not all stores from variantStores
  const requestPayload = useMemo(() => {
    return buildRemoveRequestPayload(selectedStores, storeGroupCells, blockedCellKeys, storeProducts, storesAndGroups);
  }, [storeGroupCells, blockedCellKeys, selectedStores, storeProducts, storesAndGroups]);

  // Calculate unique store count for processing state
  const uniqueStoreCount = useMemo(() => {
    return requestPayload.stores.length;
  }, [requestPayload]);

  const handleConfirmDeactivation = () => {
    if (!isBlockedStateReady || isBlockedStateError) {
      return;
    }

    if (requestPayload.variantIds.length === 0 || requestPayload.stores.length === 0) {
      return;
    }
    setIsDeactivating(true);
    removeProduct(requestPayload, {
      onSuccess: (response) => {
        setDeactivationResponse(response as PriceUpdateAPIResponse);
        setDeactivationComplete(true);
        setIsDeactivating(false);
        // Call afterSave immediately to refresh data, just like EditPriceDrawer does
        afterSave();
      },
      onError: (error: any) => {
        // Handle 504 Gateway Timeout errors - request is still processing on backend
        if (
          error?.response?.status === 504 ||
          error?.code === 'ECONNABORTED' ||
          error?.message?.includes('timeout') ||
          error?.message?.includes('Gateway Timeout')
        ) {
          // Create a timeout response object that EditDrawerSuccessAndFailure can handle
          const timeoutResponse = {
            failedStores: [],
            status: 'TIMEOUT',
            message: t('requestProcessingModal.timeout.message'),
          } as unknown as PriceUpdateAPIResponse;
          setDeactivationResponse(timeoutResponse);
          setDeactivationComplete(true);
          setIsDeactivating(false);
          // Call afterSave to refresh data and clear selections even on timeout
          afterSave();
          return;
        }
        // Handle other errors (exceptions, backend errors, no response)
        // Create an error response object that EditDrawerSuccessAndFailure can handle
        const errorResponse = {
          // successStoresCount is missing/undefined - this triggers the allMenusFailedTitle display
          failedStores: [],
          status: 'ERROR',
          message: error?.message || t('deactivateProducts.errorMessage'),
        } as unknown as PriceUpdateAPIResponse;
        setDeactivationResponse(errorResponse);
        setDeactivationComplete(true);
        setIsDeactivating(false);
        // Call afterSave to refresh data and clear selections even on error
        afterSave();
      },
    });
  };

  const onCloseDrawer = () => {
    // Reset all states when closing the drawer
    setDeactivationComplete(false);
    setIsDeactivating(false);
    setDeactivationResponse(undefined);
    setState(false);
    onClose();
  };

  return (
    <div>
      <SwipeableDrawer open={state} onClose={onCloseDrawer} onOpen={() => {}} anchor="bottom">
        <div className={styles.container}>
          <header>
            <div>
              <h1>{t('deactivateProducts.title')}</h1>
            </div>
            <button type="button" onClick={onCloseDrawer}>
              <X data-testid="x-icon" size={24} color="var(--black-900)" />
            </button>
          </header>
          {(isDeactivating || (deactivationComplete && deactivationResponse)) && (
            <div className={styles.alertBanner}>
              <CalendarAlert />
            </div>
          )}

          <div className={styles.content}>
            {isDeactivating ? (
              <EditDrawerProcessing count={uniqueStoreCount} entityToUpdate={EntityToUpdate.MENU} />
            ) : deactivationComplete && deactivationResponse ? (
              <div className={styles.successFailureWrapper}>
                <EditDrawerSuccessAndFailure
                  apiResponse={deactivationResponse as PriceUpdateAPIResponse}
                  entityToUpdate={EntityToUpdate.MENU}
                />
              </div>
            ) : isFetchingBlockedState ? (
              <div className={styles.loadingContent} data-testid="blocked-state-loading">
                <Loader className={styles.loadingSpinner} size={32} />
                <p className={styles.loadingMessage}>{t('common.loading')}</p>
              </div>
            ) : (
              <>
                <div className={styles.confirmationContent}>
                  <div className={styles.confirmationTitleSection}>
                    <h2 className={styles.confirmationTitle}>{confirmationTitle}</h2>
                    <div className={styles.confirmationDivider} />
                    <p className={styles.deactivationInfo}>{infoText}</p>
                  </div>

                  {isBlockedStateError && (
                    <p className={styles.blockedStateError} data-testid="blocked-state-error">
                      {t('deactivateProducts.activeBundlesCheckError')}
                    </p>
                  )}

                  <div className={styles.tableWrapper}>
                    <ProductsTable
                      storeProducts={storeProducts}
                      setPriceDeviationsData={setPriceDeviationsData}
                      blockedCellKeys={blockedCellKeys}
                      activeBundleStoreGroups={resolvedActiveBundleStoreGroups}
                      isActiveBundlesLoading={false}
                      isActiveBundlesError={isBlockedStateError}
                    />
                  </div>
                </div>

                <div className={styles.footer}>
                  <div className={styles.alertContainer}>
                    <CalendarAlert />
                  </div>

                  {hasBlocked && (
                    <div className={styles.blockedWarning} data-testid="blocked-warning-bubble">
                      <span className={styles.blockedWarningIcon}>
                        <Icon
                          iconName="AlertCircle"
                          size={18}
                          style={{ color: 'var(--red-700)', stroke: 'var(--red-700)' }}
                        />
                      </span>
                      <span className={styles.blockedWarningText}>{t('deactivateProducts.blockedWarningBubble')}</span>
                    </div>
                  )}

                  <div className={styles.buttonContainer}>
                    <Button
                      className={styles.cancelButton}
                      onClick={onCloseDrawer}
                      variant={ButtonVariants.Secondary}
                      type="button"
                      disabled={isDeactivating}
                    >
                      {t('editMenuPrice.cancel')}
                    </Button>
                    <Button
                      className={styles.submitButton}
                      onClick={handleConfirmDeactivation}
                      variant={ButtonVariants.Primary}
                      type="button"
                      isLoading={isDeactivating}
                      disabled={isDeactivating || !hasEligible || isBlockedStateError}
                    >
                      {t('deactivateProducts.submitChanges')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </SwipeableDrawer>
    </div>
  );
};
